/**
 * Internal Analytics Event Queue
 *
 * Batches analytics events and flushes them to the backend via the analytics-events API proxy.
 * Uses sendBeacon for reliability on page unload, with fetch fallback.
 */

import { detectClient, getClientSessionId, ClientType } from './clientDetection'
import { getDeviceSummary } from './deviceDetection'

interface AnalyticsEvent {
  event_name: string
  event_category?: string
  event_id?: string
  properties?: Record<string, any>
  booking_session_id?: string
  booking_step?: string
  timestamp?: string
}

interface DeviceData {
  device_type: string
  os: string
  os_version: string
  browser: string
  browser_version: string
  model: string
  screen_width: number
  screen_height: number
  viewport_width: number
  viewport_height: number
  pixel_ratio: number
  touch_support: boolean
  language: string
  timezone: string
}

interface QueuedEvent extends AnalyticsEvent {
  client: ClientType
  client_session_id: string
  session_id: string
  user_id: string
  timestamp: string
  page_url: string
  page_title: string
  referrer: string
  user_agent: string
  device: DeviceData
}

const FLUSH_INTERVAL = 5000 // 5 seconds
const MAX_BATCH_SIZE = 15
const MAX_QUEUE_SIZE = 500
const MAX_RETRY_COUNT = 3
const BEACON_SIZE_LIMIT = 60 * 1024 // 60KB

let queue: QueuedEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let sessionId: string = ''
let userId: string = ''
let cachedDeviceData: DeviceData | null = null

function getSessionId(): string {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('analytics_session_id') || ''
    if (!sessionId) {
      sessionId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
      sessionStorage.setItem('analytics_session_id', sessionId)
    }
  }
  return sessionId
}

function getUserId(): string {
  if (!userId) {
    userId = localStorage.getItem('analytics_main_user_id') || ''
  }
  return userId
}

/**
 * Get device data (cached for performance)
 */
function getDeviceData(): DeviceData {
  if (!cachedDeviceData) {
    cachedDeviceData = getDeviceSummary()
  }
  return cachedDeviceData
}

/**
 * Set the user ID for analytics events.
 * Call this when user authentication state changes.
 */
export function setAnalyticsUserId(id: string) {
  userId = id
  if (typeof window !== 'undefined') {
    localStorage.setItem('analytics_main_user_id', id)
  }
}

/**
 * Clear the stored user ID (e.g., on logout).
 */
export function clearAnalyticsUserId() {
  userId = ''
  if (typeof window !== 'undefined') {
    localStorage.removeItem('analytics_main_user_id')
  }
}

/**
 * Enqueue an analytics event for batched delivery.
 * Events are flushed automatically when the batch size is reached
 * or after FLUSH_INTERVAL milliseconds.
 */
export function enqueueEvent(event: AnalyticsEvent) {
  if (typeof window === 'undefined') return

  // Extract event_id from properties if not provided at the top level
  const resolvedEventId = event.event_id || event.properties?.event_id


  const queuedEvent: QueuedEvent = {
    ...event,
    event_id: resolvedEventId,
    client: detectClient(),
    client_session_id: getClientSessionId(),
    session_id: getSessionId(),
    user_id: getUserId(),
    timestamp: event.timestamp || new Date().toISOString(),
    page_url: window.location.href,
    page_title: document.title,
    referrer: document.referrer,
    user_agent: navigator.userAgent,
    device: getDeviceData(),
  }

  // Drop oldest events if queue exceeds MAX_QUEUE_SIZE to prevent memory issues
  if (queue.length >= MAX_QUEUE_SIZE) {
    queue.splice(0, queue.length - MAX_QUEUE_SIZE + 1)
  }

  queue.push(queuedEvent)

  if (queue.length >= MAX_BATCH_SIZE) {
    flush()
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL)
  }
}

/**
 * Immediately flush all queued events to the backend.
 * Called automatically on batch full, visibility change, or page unload.
 */
export function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  if (queue.length === 0) return

  const batch = queue.splice(0, MAX_BATCH_SIZE)

  sendBatch(batch)

  // If there are remaining events, schedule another flush
  if (queue.length > 0 && !flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL)
  }
}

function sendBatch(batch: QueuedEvent[], retryCount = 0) {
  const payload = JSON.stringify({ events: batch })
  const payloadBytes = new TextEncoder().encode(payload).length

  // If payload exceeds 60KB, split into two halves and send separately
  if (payloadBytes > BEACON_SIZE_LIMIT && batch.length > 1) {
    const mid = Math.ceil(batch.length / 2)
    sendBatch(batch.slice(0, mid), retryCount)
    sendBatch(batch.slice(mid), retryCount)
    return
  }

  // Use sendBeacon for reliability (works on page unload)
  const sent = navigator.sendBeacon?.('/api/analytics-events', new Blob([payload], { type: 'application/json' }))
  

  if (!sent) {
    // Fallback to fetch
    fetch('/api/analytics-events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(err => {
      console.warn('Analytics flush failed:', err)
      // Re-queue events at the front of the queue if under retry limit
      if (retryCount < MAX_RETRY_COUNT) {
        setTimeout(() => sendBatch(batch, retryCount + 1), 1000 * (retryCount + 1))
      }
    })
  }
}

/**
 * Track a booking funnel step transition.
 * Convenience method for booking-specific events.
 */
export function trackBookingStep(
  bookingSessionId: string,
  stepName: string,
  properties?: Record<string, any>
) {
  enqueueEvent({
    event_name: 'booking_step',
    event_category: 'booking_funnel',
    booking_session_id: bookingSessionId,
    booking_step: stepName,
    properties,
  })
}

/**
 * Track an abandoned booking (cart abandonment).
 * Should be called when user leaves during an active booking.
 */
export function trackAbandonedBooking(
  bookingSessionId: string,
  lastStep: string,
  properties?: Record<string, any>
) {
  enqueueEvent({
    event_name: 'booking_abandoned',
    event_category: 'booking_funnel',
    booking_session_id: bookingSessionId,
    booking_step: lastStep,
    properties: {
      ...properties,
      abandoned_at: new Date().toISOString(),
    },
  })
}

// Register visibility change and beforeunload handlers for reliable flush
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
  window.addEventListener('beforeunload', flush)
}
